import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { verifyCashfreePayment } from '../services/cashfreeService';
import { markPaymentSuccess } from '../services/paymentService';
import { createSubscription } from '../services/subscriptionService';
import { grantDatabaseAccess } from '../services/databaseAccessService';
import { notifyAndroidPaymentSuccess } from '../services/androidBridge';

const CashfreeReturnHandler = ({ user, userData, onPaymentVerified }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const handledOrdersRef = useRef(new Set());

  useEffect(() => {
    if (!user?.uid) return;

    const checkAndVerifyPayment = async () => {
      const searchParams = new URLSearchParams(location.search);
      const orderId = searchParams.get('order_id') || searchParams.get('orderId') || searchParams.get('cf_order_id');

      let targetOrderId = orderId;
      let pendingPaymentDoc = null;

      // If no order_id in URL, check if there is a recent pending payment in Firestore for this user
      if (!targetOrderId) {
        try {
          const paymentsQuery = query(
            collection(db, 'payments'),
            where('userId', '==', user.uid),
            where('status', '==', 'pending')
          );
          const snapshot = await getDocs(paymentsQuery);

          if (!snapshot.empty) {
            const sortedDocs = snapshot.docs.sort((a, b) => {
              const aTime = a.data().createdAt?.toMillis() || 0;
              const bTime = b.data().createdAt?.toMillis() || 0;
              return bTime - aTime;
            });

            const newestDoc = sortedDocs[0];
            const pData = newestDoc.data();
            const createdAtMillis = pData.createdAt?.toMillis() || 0;
            const fifteenMinutes = 15 * 60 * 1000;

            // Only auto-verify if created within the last 15 minutes
            if (Date.now() - createdAtMillis < fifteenMinutes) {
              targetOrderId = pData.metadata?.orderId || pData.razorpayOrderId || newestDoc.id;
              pendingPaymentDoc = { id: newestDoc.id, ...pData };
            }
          }
        } catch (err) {
          console.warn('⚠️ [CashfreeReturnHandler] Error checking pending payments:', err);
        }
      }

      if (!targetOrderId || handledOrdersRef.current.has(targetOrderId)) {
        return;
      }

      // Mark order as handled so we don't loop
      handledOrdersRef.current.add(targetOrderId);
      setVerifying(true);
      setStatusMessage('Verifying your payment with Cashfree...');

      console.log('🔍 [CashfreeReturnHandler] Verifying order ID:', targetOrderId);

      try {
        const verification = await verifyCashfreePayment({ orderId: targetOrderId });
        console.log('📊 [CashfreeReturnHandler] Verification result:', verification);

        if (verification.verified && verification.isPaid) {
          setStatusMessage('Payment verified! Activating your package...');

          // 1. Find matching payment record
          let firebasePaymentId = pendingPaymentDoc?.id;
          let paymentRecord = pendingPaymentDoc;

          if (!firebasePaymentId) {
            const q = query(
              collection(db, 'payments'),
              where('userId', '==', user.uid)
            );
            const pSnap = await getDocs(q);
            const matches = pSnap.docs.filter(d => {
              const data = d.data();
              return d.id === targetOrderId || data.metadata?.orderId === targetOrderId || data.razorpayOrderId === targetOrderId;
            });

            if (matches.length > 0) {
              firebasePaymentId = matches[0].id;
              paymentRecord = matches[0].data();
            }
          }

          // 2. Mark payment as successful in Firestore
          if (firebasePaymentId) {
            await markPaymentSuccess(firebasePaymentId, {
              paymentId: targetOrderId,
              orderId: targetOrderId
            });
          }

          // 3. Update user document with paymentId: true
          try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
              paymentId: true,
              updatedAt: Timestamp.now()
            });
            console.log('✅ [CashfreeReturnHandler] User paymentId set to true');
          } catch (uErr) {
            console.warn('⚠️ [CashfreeReturnHandler] User update error:', uErr);
          }

          // 4. Activate Subscription or Database Access based on package type
          const meta = paymentRecord?.metadata || {};
          const pkgType = meta.packageType || (paymentRecord?.packageName?.toLowerCase().includes('database') ? 'database' : 'employer');
          const packageName = paymentRecord?.packageName || 'Activated Package';

          let redirectPath = '/dashboard';

          if (pkgType === 'database') {
            await grantDatabaseAccess(user.uid, {
              id: paymentRecord?.packageId || 'db_pkg',
              name: packageName,
              maxContacts: meta.maxContacts || 20,
              durationDays: meta.durationDays || meta.duration || 30
            });
            notifyAndroidPaymentSuccess(packageName);
            redirectPath = '/database';
          } else {
            const isJobSeeker = (userData?.userType === 'jobseeker');
            await createSubscription({
              userId: user.uid,
              packageId: paymentRecord?.packageId || 'pkg',
              packageName: packageName,
              packageType: pkgType,
              price: paymentRecord?.amount || 0,
              duration: meta.duration || 30,
              maxJobPostings: meta.maxJobPostings || 0,
              featuredJobs: meta.featuredJobs || 0,
              maxApplications: meta.maxApplications || 0,
              userType: userData?.userType || (isJobSeeker ? 'jobseeker' : 'employer'),
              paymentId: firebasePaymentId || targetOrderId,
              cashfreePaymentId: targetOrderId,
              cashfreeOrderId: targetOrderId
            });
            notifyAndroidPaymentSuccess(packageName);
            redirectPath = isJobSeeker ? '/dashboard/jobseeker' : '/dashboard/employer';
          }

          // 5. Clean up URL search parameters cleanly
          if (window.history.replaceState && orderId) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }

          if (onPaymentVerified) {
            onPaymentVerified();
          }

          alert(`🎉 Payment Successful!\n\nYour package "${packageName}" is active.`);
          navigate(redirectPath, { replace: true });
        } else {
          console.warn('⚠️ [CashfreeReturnHandler] Payment not paid:', verification);
          if (orderId && window.history.replaceState) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
      } catch (error) {
        console.error('❌ [CashfreeReturnHandler] Verification error:', error);
      } finally {
        setVerifying(false);
      }
    };

    checkAndVerifyPayment();
  }, [user, userData, location.search, navigate, onPaymentVerified]);

  if (!verifying) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl animate-pulse">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h3>
        <p className="text-gray-600 text-sm">{statusMessage || 'Please wait while we unlock your content...'}</p>
      </div>
    </div>
  );
};

export default CashfreeReturnHandler;
